import { useState } from 'react';
import {
  actOnHit,
  getHits,
  getHome,
  isLocked,
  listReports,
  type MatchAction,
  type ReportOutcome,
} from '../api/endpoints.js';
import { useAction, useApi } from '../api/hooks.js';
import { monitoringStory } from '../flow/monitoring.js';
import { HitCard } from './hit-card.js';
import {
  Async,
  Badge,
  Button,
  Failure,
  Field,
  KeyValue,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

/**
 * `report_id` NARROWS FOR REAL SINCE 0026, and this screen is where that became
 * visible. It used to default to a placeholder uuid, because services have no
 * reports table — their `v_person_hits.report_id` is a typed NULL — so any uuid
 * returned the session's whole history and the field existed to demonstrate the
 * inertness.
 *
 * Now the id resolves to a week on `media.reports` and filters by `detected_at`.
 * So the field starts EMPTY and you pick a period from the list instead: that is
 * what the app does, and an id typed by hand that matches no period of yours
 * correctly returns nothing.
 */
const REPORT_ID_PLACEHOLDER = '';

/**
 * ONLY `clean` MAY CLAIM ABSENCE. `not_due` means nobody looked that week (the
 * person is on fortnightly or monthly cadence) and `incomplete` means every
 * provider failed — rendering either as "nothing found" is the false negative
 * docs/CLAUDE.md §1 calls a broken promise. The copy is per-outcome for exactly
 * that reason, and an unknown outcome falls to a line that asserts nothing.
 */
const OUTCOME_COPY: Record<ReportOutcome, { label: string; tone: 'ok' | 'warn' }> = {
  findings: { label: 'images to review', tone: 'warn' },
  clean: { label: 'no matches in monitored sources', tone: 'ok' },
  not_due: { label: 'no scan was due this week', tone: 'warn' },
  incomplete: { label: 'coverage was incomplete this week', tone: 'warn' },
};

function outcomeLine(outcome: ReportOutcome, hitsFound: number): string {
  const copy = OUTCOME_COPY[outcome];
  if (copy === undefined) return 'checked';
  return outcome === 'findings' ? `${String(hitsFound)} ${copy.label}` : copy.label;
}

/**
 * PER-PERSON, and there is no household read path into hit detail. No route here
 * accepts a person identifier in any position — the legacy
 * `GET /api/reports?phone=&userId=` took the person as a query filter, which let
 * any member read anyone's matches.
 *
 * All three actions terminate: 'infringement' and 'not_infringement' both take
 * the item out of live exposure, so reporting abuse never costs more than
 * clearing an authorised use.
 */
const ACTIONS: { action: MatchAction; label: string; meaning: string }[] = [
  { action: 'infringement', label: 'Infringement', meaning: 'this is me and it is not allowed' },
  { action: 'not_infringement', label: 'Authorised', meaning: 'this is me and it is fine' },
  { action: 'not_me', label: 'Not me', meaning: 'never my exposure — costs nothing' },
];

export function ReportsScreen() {
  // TWO READS NOW. The dated period list is GET /v1/reports; the monitoring
  // story (scan state, tier, next scan) moved to /v1/home's reports_summary when
  // 0026 gave the list route to the periods.
  const reports = useApi(listReports, []);
  const home = useApi(getHome, []);
  const [reportId, setReportId] = useState(REPORT_ID_PLACEHOLDER);
  const [note, setNote] = useState('');
  /**
   * ONE OPEN CARD AT A TIME, and this is a safety mechanism rather than a
   * layout preference: a crop may render only on the hit's own card (services
   * invariant #23), so exactly one hit can ever be in a state that fetches one.
   */
  const [openHitId, setOpenHitId] = useState<string | null>(null);

  const hits = useApi(() => getHits(reportId), [reportId], { skip: reportId.length === 0 });
  const act = useAction(actOnHit, () => {
    setNote('');
    hits.reload();
    reports.reload();
    home.reload();
  });

  return (
    <Screen
      title="Reports & hits"
      lede="Served from the svc contract views that services shipped in migration 0016 — the backend reads the views and forwards the write."
    >
      {/* THE PAYWALL IS A 200 NOW (0026), NOT A 402. The four DETAIL routes
          still refuse; the list sells instead, because the digest email already
          told this person to subscribe and an API that refuses what the email
          advertises is the worse surface. Counts travel; nothing that says
          WHERE does. */}
      <Panel title="reports">
        <Async state={reports}>
          {(data) => (
            <>
              {isLocked(data) && (
                <div style={{ marginBottom: 12 }}>
                  <p style={{ margin: '0 0 4px' }}>
                    <strong>{data.upsell.headline}</strong>
                  </p>
                  <p className="muted" style={{ margin: '0 0 6px' }}>
                    {data.upsell.body}
                  </p>
                  <Row>
                    <Badge tone="warn">locked — {data.locked_reason ?? 'no plan'}</Badge>
                    <a href="#/plans">{data.upsell.cta}</a>
                  </Row>
                  <p className="muted" style={{ marginTop: 6 }}>
                    Coverage and scanning are already running — a plan changes what you can SEE, not
                    whether you are watched. The counts below are real.
                  </p>
                </div>
              )}

              {data.reports.length === 0 ? (
                <p className="empty">
                  no reports yet — one closes per week, and the first arrives as soon as your first
                  scan completes rather than waiting for Sunday
                </p>
              ) : (
                <table className="rows">
                  <tbody>
                    {data.reports.map((report) => {
                      const selected = report.report_id === reportId;
                      return (
                        <tr key={report.report_id}>
                          <td>
                            {/* Dates are rendered VERBATIM. They are YYYY-MM-DD
                                strings in UTC; new Date(...) on a bare date is
                                parsed as UTC then printed local, which shows the
                                previous day west of Greenwich. The API already
                                decided what week this is. */}
                            <code>{report.period_start}</code>
                            <span className="muted"> → </span>
                            <code>{report.period_end}</code>
                            {report.is_first_report && (
                              <span className="muted"> (first — a partial week)</span>
                            )}
                          </td>
                          <td>
                            <Badge tone={OUTCOME_COPY[report.outcome]?.tone ?? 'warn'}>
                              {report.outcome}
                            </Badge>
                          </td>
                          <td>{outcomeLine(report.outcome, report.hits_found)}</td>
                          <td>
                            {isLocked(data) ? (
                              <span className="muted">subscribe to open</span>
                            ) : (
                              <Button
                                onClick={() => {
                                  setReportId(selected ? '' : report.report_id);
                                  setOpenHitId(null);
                                }}
                              >
                                {selected ? 'close' : 'open'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <p className="muted" style={{ marginTop: 10 }}>
                {data.scope_note}
              </p>
              <Raw label="GET /v1/reports" value={data} />
            </>
          )}
        </Async>
      </Panel>

      {/* The monitoring story moved to /v1/home when the list route became the
          periods. Same fields, same source — reports_summary is what the
          dashboard renders its monitoring copy from, and it is derived, never
          asserted. */}
      <Panel title="monitoring">
        <Async state={home}>
          {(data) => {
            const summary = data.reports_summary;
            /**
             * NULL IS NOT ZERO, AND COALESCING IT WOULD BE A FALSE
             * REASSURANCE. /v1/home withholds the hit counts while locked
             * (0023) rather than zeroing them. monitoringStory branches on
             * `activeHits > 0` FIRST, so passing 0 for a withheld count would
             * hand a locked person with real findings the clean-scan headline —
             * precisely the false negative docs/CLAUDE.md §1 forbids.
             *
             * So the story is only told when the numbers are actually there.
             * Locked gets the scan truth, which is not withheld and is not a
             * claim about what was found.
             */
            const counts =
              summary.active_hits === null || summary.live_exposure_count === null
                ? null
                : { active: summary.active_hits, live: summary.live_exposure_count };
            const story =
              counts === null
                ? null
                : monitoringStory({
                    scanState: summary.scan_state,
                    lastRunAt: summary.last_run_at,
                    firstScanCompletedAt: summary.first_scan_completed_at,
                    monitoredSources: summary.monitored_sources,
                    nextScanAfter: summary.next_scan_after,
                    unavailableReason: summary.unavailable_reason,
                    activeHits: counts.active,
                    liveExposureCount: counts.live,
                    totalHits: counts.active,
                  });
            return (
              <>
                {story === null ? (
                  <p className="muted" style={{ margin: '0 0 12px' }}>
                    The hit summary is withheld while the reports surface is locked — withheld, not
                    zero. Scanning below is running either way.
                  </p>
                ) : (
                  <p style={{ margin: '0 0 12px' }}>
                    <strong>{story.headline}</strong>
                    <span className="muted" style={{ display: 'block', marginTop: 4 }}>
                      {story.detail}
                    </span>
                  </p>
                )}
                <KeyValue
                  rows={[
                    ['scan_state', <Badge key="s">{summary.scan_state}</Badge>],
                    ['scan_tier', summary.scan_tier],
                    ['last_run_at', summary.last_run_at],
                    ['first_scan_completed_at', summary.first_scan_completed_at],
                    ['next_scan_after', summary.next_scan_after],
                    ['monitored_sources', summary.monitored_sources],
                    ['unavailable_reason', summary.unavailable_reason],
                    ['active_hits', summary.active_hits],
                    ['live_exposure_count', summary.live_exposure_count],
                  ]}
                />
              </>
            );
          }}
        </Async>
      </Panel>

      <Panel title="hits">
        <Row>
          <Field
            label="report_id (from the list above)"
            value={reportId}
            onChange={setReportId}
          />
        </Row>
        {reportId.length === 0 ? (
          <p className="empty">
            open a report above to list its hits — since 0026 the id resolves to a week and filters
            by detected_at, so it narrows the session’s own hits for real; an id that matches no
            period of yours returns nothing rather than everything
          </p>
        ) : (
          <Async state={hits}>
            {(data) => (
              <>
                {data.hits.length === 0 && <p className="empty">no hits for this report</p>}
                {data.hits.map((hit) => (
                  <div key={hit.hit_id}>
                    {/* THE ASK-CARD. A machine-triaged hit is a question put to
                        the person in the photo, and their answer IS the
                        decision — there is no operator in between any more. */}
                    <HitCard
                      hit={hit}
                      isOpen={openHitId === hit.hit_id}
                      onToggle={() => {
                        setOpenHitId(openHitId === hit.hit_id ? null : hit.hit_id);
                      }}
                      onDecided={() => {
                        // A decision changes the card AND the counts.
                        setOpenHitId(null);
                        hits.reload();
                        reports.reload();
                        home.reload();
                      }}
                    />
                    <Row>
                      <Badge>{hit.hit_status}</Badge>
                      <code>{hit.hit_id}</code>
                      <span className="muted">last checked {hit.last_checked_at ?? 'never'}</span>
                      {hit.decided_at !== null && (
                        <span className="muted">
                          decided {new Date(hit.decided_at).toLocaleString()}
                        </span>
                      )}
                    </Row>

                    {hit.matches.length === 0 ? (
                      <p className="empty">no matches on this hit</p>
                    ) : (
                      <div className="scroll-x">
                        <table>
                          <thead>
                            <tr>
                              <th>source_domain</th>
                              <th>detected_at</th>
                              <th>status</th>
                              <th>action</th>
                              <th>lifecycle</th>
                              <th>score</th>
                              <th>providers</th>
                              <th>resolved_at</th>
                            </tr>
                          </thead>
                          <tbody>
                            {hit.matches.map((match) => (
                              <tr key={match.match_id}>
                                <td>{match.source_domain ?? '—'}</td>
                                <td>{match.detected_at ?? '—'}</td>
                                <td>{match.match_status ?? '—'}</td>
                                <td>{match.match_action ?? <span className="muted">none</span>}</td>
                                <td>{match.match_lifecycle ?? '—'}</td>
                                <td>{match.score ?? '—'}</td>
                                <td>{match.provider_count ?? '—'}</td>
                                <td>{match.resolved_at ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* THE OTHER LANE, and it is deliberately not beside the
                        two answer buttons. Feedback records what the user said
                        and DECIDES NOTHING; the ask-card's yes/not-me writes a
                        decision that cannot be re-decided. Five buttons in one
                        row would read as one choice of five. */}
                    <details className="raw">
                      <summary>record feedback instead (decides nothing)</summary>
                      <Row>
                        <Field label="note (optional)" value={note} onChange={setNote} />
                        {ACTIONS.map((entry) => (
                          <Button
                            key={entry.action}
                            variant="secondary"
                            onClick={() => act.run(hit.hit_id, entry.action, note)}
                            disabled={act.pending}
                          >
                            {entry.label}
                          </Button>
                        ))}
                      </Row>
                      <p className="muted">
                        {ACTIONS.map((entry) => `${entry.label}: ${entry.meaning}`).join(' · ')}
                      </p>
                    </details>
                  </div>
                ))}
                <p className="muted" style={{ marginTop: 10 }}>
                  {data.scope_note}
                </p>
                <Raw label="GET /v1/reports/{id}/hits" value={data} />
              </>
            )}
          </Async>
        )}
        {act.error !== null && (
          <Failure
            error={act.error}
            hint={
              act.error.code === 'HIT_NOT_FOUND'
                ? 'Unknown hit, or one that is not yours — the same answer either way, deliberately.'
                : undefined
            }
          />
        )}
        {act.result !== null && <Raw label="action response" value={act.result} />}
      </Panel>
    </Screen>
  );
}
